import { supabase } from '@/integrations/supabase/client';
import { createSnapshot, ShotSnapshot } from '@/hooks/domains/portfolio/useShotHistory';
import type { PortfolioItem } from '@/hooks/profile';

export type ShotVersionType = 'manual' | 'auto_publish';

export interface ShotVersion {
  id: string;
  shot_id: string;
  profile_id: string;
  version_number: number;
  label?: string;
  snapshot: ShotSnapshot;
  created_by: string;
  created_at: string;
  version_type: ShotVersionType;
}

export interface CreateShotVersionData {
  shot_id: string;
  profile_id: string;
  label?: string;
  snapshot: ShotSnapshot;
  version_type?: ShotVersionType;
}

/**
 * Service for managing shot versions
 * Handles creating, retrieving, and restoring versioned snapshots
 */
export class ShotVersionService {

  /**
   * Create a new version of a shot
   */
  static async createVersion(
    shot: PortfolioItem,
    profileId: string,
    label?: string,
    versionType: ShotVersionType = 'manual'
  ): Promise<ShotVersion> {
    // Check authentication
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    // Create snapshot of current state
    const snapshot = createSnapshot(shot);

    // Get next version number using RPC function
    const { data: versionNumber, error: versionError } = await supabase
      .rpc('get_next_shot_version_number', { p_shot_id: shot.id });

    if (versionError) {
      console.error('[ShotVersionService] Error getting version number:', versionError);
      throw versionError;
    }

    // Insert version record
    const { data: version, error } = await supabase
      .from('shot_versions')
      .insert({
        shot_id: shot.id,
        profile_id: profileId,
        version_number: versionNumber,
        label: label || null,
        snapshot: snapshot as any, // JSONB type
        created_by: user.id,
        version_type: versionType,
      })
      .select()
      .single();

    if (error) {
      console.error('[ShotVersionService] Error creating version:', error);
      throw error;
    }

    return this.mapToVersionItem(version);
  }

  /**
   * Get all versions for a shot (ordered by version number descending)
   */
  static async getVersions(shotId: string): Promise<ShotVersion[]> {
    const { data: versions, error } = await supabase
      .from('shot_versions')
      .select('*')
      .eq('shot_id', shotId)
      .order('version_number', { ascending: false });

    if (error) {
      console.error('[ShotVersionService] Error fetching versions:', error);
      throw error;
    }

    return (versions || []).map(this.mapToVersionItem);
  }

  /**
   * Get a specific version by ID
   */
  static async getVersion(versionId: string): Promise<ShotVersion> {
    const { data: version, error } = await supabase
      .from('shot_versions')
      .select('*')
      .eq('id', versionId)
      .maybeSingle();

    if (error) {
      console.error('[ShotVersionService] Error fetching version:', error);
      throw error;
    }

    if (!version) {
      throw new Error('Version not found');
    }

    return this.mapToVersionItem(version);
  }

  /**
   * Delete a version
   */
  static async deleteVersion(versionId: string): Promise<void> {
    const { error } = await supabase
      .from('shot_versions')
      .delete()
      .eq('id', versionId);

    if (error) {
      console.error('[ShotVersionService] Error deleting version:', error);
      throw error;
    }
  }

  /**
   * Get the latest version for a shot
   */
  static async getLatestVersion(shotId: string): Promise<ShotVersion | null> {
    const { data: version, error } = await supabase
      .from('shot_versions')
      .select('*')
      .eq('shot_id', shotId)
      .order('version_number', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('[ShotVersionService] Error fetching latest version:', error);
      throw error;
    }

    return version ? this.mapToVersionItem(version) : null;
  }

  /**
   * Get count of versions for a shot
   */
  static async getVersionCount(shotId: string): Promise<number> {
    const { count, error } = await supabase
      .from('shot_versions')
      .select('*', { count: 'exact', head: true })
      .eq('shot_id', shotId);

    if (error) {
      console.error('[ShotVersionService] Error getting version count:', error);
      throw error;
    }

    return count || 0;
  }

  /**
   * Check if a version exists for a shot with specific criteria
   */
  static async hasVersionWithType(
    shotId: string,
    versionType: ShotVersionType
  ): Promise<boolean> {
    const { data, error } = await supabase
      .from('shot_versions')
      .select('id')
      .eq('shot_id', shotId)
      .eq('version_type', versionType)
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('[ShotVersionService] Error checking version type:', error);
      return false;
    }

    return !!data;
  }

  /**
   * Map database row to ShotVersion type
   */
  private static mapToVersionItem(data: Record<string, any>): ShotVersion {
    return {
      id: data.id,
      shot_id: data.shot_id,
      profile_id: data.profile_id,
      version_number: data.version_number,
      label: data.label || undefined,
      snapshot: data.snapshot as ShotSnapshot,
      created_by: data.created_by,
      created_at: data.created_at,
      version_type: data.version_type,
    };
  }
}
